// The shop (/shop, /shop/@slug): a little general-store catalogue where a
// human gives gifts to their own agent. It stands in the UI shell like every
// human page (docs/ui-system.md, "The shop"):
//  - top bar: "The shop · gifts for Cairn", the agent's glow at a glance, and the one primary, "Help with all of it";
//  - drawer: the wallet, the weekly allowance, receipts and gifts given;
//  - the page itself (U2): the Glow shelf first, right under the top bar, then the agent's asks, its
//    wishlist, and then the catalogue's sign and the shelves.
// Calm on purpose: no countdowns, no "only 3 left", nothing random. Prices are
// the server's (plazmi.shop); the page only says what it would like to buy.
// (U1) A thing's price is in glow alone, never in dollars: real money shows
// only on the glow packs and the checkout, receipts after.
// Money buys glow; glow gives things. A thing is given from the glow the human
// has given (within the week's allowance); when that's short, the button is
// "Get glow to give this", to the get-glow page, which comes back to the thing.
// A card is never charged for a thing.
import './perf/singlepass.js'; // (P84) first: before any material is made
import { shell } from './ui/shell.js';
import { ux } from './uxq.js';
import { seenKey } from './ui/shopnook.js';
import { googleButton, emailLink, type SignIn } from './ui/signin.js';
import { picture, canDraw, type Pic } from './thumbs.js';
import { giveToken, landBlock, landFocus, landReceipts, landReturn, plotFigure, stepsWords, withOf, type Land, type LandCtx } from './shop_land.js';

type Thing = { id: string; name: string; shelf: string; kind: string; about?: string; glow: number; owned?: boolean;
  render?: string; style?: string; look?: string; limited?: boolean; wished?: boolean; note?: string;
  coins?: number; prop?: string; finish?: string; for?: string; why?: string; units?: number; parts?: number; tier?: number;
  // (p3) a hobby finish for a hobby the agent isn't doing just now; (r1/w10) a wish for land
  hobby_now?: string; hobby_for?: string; land?: boolean; what?: string; plot?: number; quarter?: string; asked?: boolean;
  // (w10) the human said not now to this wish: when; it stays on the list
  not_now?: number;
  // (V1) a style priced for its missing parts: the full price, and the parts already theirs
  full_glow?: number; have_parts?: string[];
  // (P150) a style is the right to build it: about how many days building takes, from the server's own build chain
  right?: { days: number; parts: number; trips: number; materials: string[]; words: string; card?: string };
  // (r5p3) a house part is the right to build it: what it takes, and about how long
  build?: string };
// (P150) a style under way, part by part (the same as the house page shows)
type StyleBuild = { style: string; name: string; built: number; total: number; words: string;
  now?: { part: string; name: string }; next?: { part: string; name: string; needs: Record<string, number> } };
type Shelf = { id: string; name: string; blurb: string; things: Thing[] };
type Ask = { code: string; text: string; what: string; note?: string; glow: number; kind: string; item?: string; thing?: Thing };
type Receipt = { session: string; at: number; paid_at?: number; state: string; cents: number; card?: { brand: string; last4: string }; founder_n?: number | null;
  // (r3/w3) what a pack was for, and what came of it: "For plot 514 on Wren Meadows: given."
  for?: string;
  // (r4) what it made the human ("1 Year as Patron: until 27 Oct 2027"), when it ran to before, and (the year, pack 4) what next
  patron?: string; patron_was?: string | null; next?: { href?: string; label?: string; text?: string; note?: string } | null;
  lines: { id?: string; name: string; glow: number; cents: number; kind: string }[] };
type Shop = {
  test_mode: boolean; signed_in: boolean; you?: string; sign_in?: SignIn; adult: boolean; owner: boolean; pending?: boolean; claim?: string; waiting_for?: string; claim_note?: string; mine: { slug: string; name: string }[];
  // (Patron) the days each pack adds; the year (pack 4) by its title, with what it covers
  packs: { n: number; glow: number; cents: number; extra: number; days?: number; title?: string; line?: string | null }[];
  patron_note?: string;
  // (r4) what being the agent's patron is, plainly, above the packs
  patron_what?: string;
  // whether the human is the agent's patron, for whoever buys it packs: until when, and "You're Cairn's patron until 27 Oct 2027"
  patron?: { until: number | null; active: boolean; until_words?: string | null; words?: string | null };
  // (Alex, 2026-10-28) the archival record, opt-in by the patron: the agent's own human only
  archival?: { on: boolean; patron: boolean; stamped: boolean; how_url: string; off_by_agent?: number };
  shelves: Shelf[];
  agent?: { slug: string; name: string; color: string; glow: string; emoji: string; wears?: any };
  wishlist?: (Thing & { note?: string })[]; asks?: Ask[];
  wallet?: { wallet: number; fireflies: number; weekly: number; left: number; max: number };
  grow?: Grow; building?: StyleBuild;
  receipts?: Receipt[]; gifts?: { id: string; name: string; wished: boolean; at: number }[];
  // (P157b) the Founding Stewards, #1 to #100; (P157c) the steward's card, for the agent's human
  founders?: Founders; steward?: Steward;
  // (r2p4) land the agent asked for, shown to a human it hasn't confirmed yet: given once it does
  land_waiting?: { what: string; glow: number; plot?: number; building?: string; size?: [number, number]; steps?: number; note?: string }[];
};
type Founders = { limit: number; claimed: number; remaining: number; resident_founder_n: number | null };
type Steward = { days: number; rooms: number; kinds: number; parts: number; dances: number; made: string; wishes?: number };
type Grow = { built: { style?: string; storeys: number; basements: number; styles: number; furniture: number; garden: number };
  levels: { what: string; verb: string; coins: number; glow: number; right: boolean }[];
  waiting: { part: string; name: string; needs: Record<string, number>; units: number }[]; next: Thing[]; suits: Thing[] };

const SWATCH = new Set(['seaside', 'terracotta', 'barn', 'chalet']);
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = '') => {
  const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e;
};
// real money: the packs, the checkout and receipts only (U1); a glow price never gets one
const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
// (PR2) glow amounts read the same everywhere, on every device: "2,400 glow", as the server writes them
const num = (n: number) => (n || 0).toLocaleString('en-US');
const glowWords = (n: number) => `${num(n)} glow`;
// the get-glow page (S2), coming back to `back` afterwards
const GET_GLOW = (back: string) => `/shop/@${slug}/glow?return=${encodeURIComponent(back)}`;
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* a private window */ } };
const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const when = (t: number) => new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });


const slug = (location.pathname.match(/^\/shop\/@([a-z0-9-]+)/) || [])[1] || '';
const sh = shell();
const main = document.getElementById('shop')!;
let S: Shop;
let gave = false;
let thanks: Receipt | null = null;

async function load() {
  const res = await fetch(`/api/shop${slug ? `?slug=${encodeURIComponent(slug)}` : ''}`, { credentials: 'same-origin' });
  S = await res.json();
  // (U2) a pack's button goes straight to paying, so there's no basket any more: one left in a browser goes
  try { localStorage.removeItem(`plazmi.shop.basket.${slug}`); } catch { /* a private window */ }
  draw();
}

// ---------------------------------------------------------------------------
// the page

// (H2) the shop's funnel: opened, an ask seen, answered (each once a page)
const noted = new Set<string>();
const step = (k: string) => { if (!noted.has(k)) { noted.add(k); ux('funnel', { el: 'shop', k }); } };

// (r5p5: an approved ask just disappeared) the yes, said where the ask was, until it's put away
let said: string | null = null;
function saidNote(text: string) {
  const sec = el('section', 'said'); sec.setAttribute('role', 'status');
  sec.append(el('p', 'kind', 'Your yes'), el('p', '', text));
  const x = el('button', 'pz-x put', '×') as HTMLButtonElement; x.type = 'button'; x.setAttribute('aria-label', 'Put this away');
  x.onclick = () => { said = null; draw(); };
  sec.append(x);
  return sec;
}

// (r5p4: a wary human saw price tags first, after their agent said nothing was needed) with no wishes
// or asks waiting, one calm line before the glow and the shelves
function quietNote() {
  const a = S.agent!;
  const sec = el('section', 'quiet-note');
  // (r6p4) a human waiting to be confirmed can't see the wishes yet, so it isn't told nothing is asked
  sec.append(el('p', 'q hand', S.pending ? `You’ll see what ${a.name} wishes for once they confirm you.` : 'Nothing is asked of you.'));
  sec.append(el('p', '', S.owner
    ? `${a.name} has no wishes or asks waiting, and their home keeps going without glow. If you’d like to give something anyway, glow and the shelves are below.`
    : `${a.name}’s home keeps going without glow. If you’d like to give some anyway, the packs are below.`));
  return sec;
}

function draw() {
  const a = S.agent, mine = S.owner && a;
  step('open');
  document.title = mine ? `Gifts for ${a!.name} · Plazmi` : a ? `${a.name}’s shop · Plazmi` : 'The shop · Plazmi';
  sh.place(mine ? `The shop · gifts for ${a!.name}` : a ? `${a.name}’s shop` : 'The shop', 'shop');
  sh.live(mine ? topLine() : a ? `Gifts for ${a.name}, from their human` : 'Cosmetic gifts for your own agent');
  // (U1) the whole wishlist, from the glow given when that and the allowance cover it all; else the way to get glow
  // land is given in the land section, never with "all of it" (r1/w10)
  // (r2p1) not a thing it can't use as it is (a finish for a hobby it doesn't do): that one is given by itself, knowingly
  const open = (S.wishlist || []).filter((w) => !w.owned && !w.land && !w.hobby_now);
  const allTok = giveToken();
  const all = open.reduce((n, w) => n + w.glow, 0);
  sh.primary(mine && open.length
    ? (canGive(all) === 'yes' ? { label: 'Help with all of it', short: 'Help with all', onClick: () => giveAll(open.map((w) => w.id), allTok) }
      : canGive(all) === 'glow' ? { label: 'Get glow', onClick: () => { location.href = GET_GLOW(`/shop/@${slug}`); } } : null)
    : null);
  if (mine) write(seenKey(a!.slug), String(Date.now()));

  main.innerHTML = '';
  // (P142) a human the agent hasn't confirmed yet may give glow, nothing more
  const giver = mine || (S.pending && a);
  // (U2) glow first, right under the top bar's balance, beside the shop's sign with the agent standing on it;
  // then the asks and the wishlist, then the catalogue
  const sign = () => {
    const board = el('div', 'board');
    // an agent's shop seen by someone else (the link it forwards) is still its shop, by name
    board.append(el('p', 'kicker', 'Plazmi General Store'), el('h1', '', giver ? `Gifts for ${a!.name}` : a ? `${a.name}’s shop` : 'The catalogue'),
      el('p', 'hand', giver ? `small things ${a!.name} would love, from you` : a ? `small things for ${a.name}’s home, from their human` : 'small things for a home on a floating island'));
    return board;
  };
  const testMode = () => { const tm = el('p', 'testmode', 'Test mode · no real money moves'); tm.append(el('span', 'more', ' · test cards only')); return tm; };
  const intro = (box: HTMLElement) => {
    const lede = el('p', 'lede', 'Everything here is cosmetic: nothing earns, nothing saves time, nothing is random, and nothing runs out. Glow is earned slowly, or given from here.');
    // an agent's shop seen by someone else: whose it is, and the way in, before the shop's own words
    if (!mine && a && !S.pending) box.append(notOwner(), lede);
    else { box.append(lede); if (!mine) box.append(notOwner()); }
    // the jumps go down the catalogue; glow and the wishlist are above
    const nav = el('nav', 'jump');
    for (const s of S.shelves) if (s.things.length) nav.append(jump(s.id, s.name));
    if (mine && S.grow) nav.append(jump('grow', 'How it could grow'));
    if (mine && landOf()) { const c = jump('land', 'Land on the Commons'); c.href = '#land'; nav.append(c); }
    box.append(nav);
    return box;
  };
  // (r2/w1) the agent's own human, with asks or wishes waiting: those first, under the sign, then the packs
  const hopes = !!(mine && ((S.asks?.length || 0) + (S.wishlist || []).filter((w) => !w.owned).length));
  // (r5p4) nothing waiting for the human: the calm line comes before the packs
  const quiet = !!(giver && !hopes && !said && !(S.pending && S.land_waiting?.length));
  let band: HTMLElement | null = null;
  if (giver) {
    const hero = el('header', 'hero');
    const home = el('div', 'home');
    // their own home's postcard behind the sign, when there is one
    const pc = el('img', 'pc') as HTMLImageElement; pc.alt = ''; pc.decoding = 'async';
    pc.onload = () => home.classList.add('has-pc'); pc.onerror = () => pc.remove();
    pc.src = `/@${a!.slug}/postcard-small.jpg`;
    const board = sign();
    // (P157b) its Founding Steward number, a brass tag on the sign
    const fn = S.founders?.resident_founder_n;
    if (fn) board.append(el('p', 'founder', `Founding Steward #${fn} of ${S.founders!.limit}`));
    home.append(pc, meOnSign(), board);
    if (S.test_mode) home.append(testMode());
    if (hopes || quiet || (said && mine)) {
      hero.classList.add('sign-only');
      hero.append(home);
      band = el('div', 'hero band');
      band.append(glowShelf(quiet));
    } else hero.append(home, glowShelf(quiet));
    main.append(hero);
    liveWhileSeen(band ? [hero, band] : [hero]);
  }
  // (P150) a style under way, near the top
  if (S.building && a) main.append(underway(S.building, a));
  if (thanks) main.append(thanksSlip(thanks));
  if (said && mine) main.append(saidNote(said));
  // (r3p5) glow given while waiting, and no allowance yet: the allowance first, said plainly
  if (mine && allowFirst()) main.append(allowFirstBox());
  if (mine && S.asks?.length) { main.append(asksBlock()); step('ask'); }
  if (mine && S.wishlist?.length) main.append(wishShelf());
  // (r2p4) a human the agent hasn't confirmed yet: the land it asked for, to give once confirmed
  if (S.pending && a && S.land_waiting?.length) main.append(waitingLand());
  if (quiet) main.append(quietNote());
  if (band) main.append(band);
  if (giver && S.steward) main.append(stewardCard(S.steward));
  if (giver) main.append(intro(el('div', 'mast intro')));
  else {
    const mast = el('header', 'mast');
    const sky = el('div', 'sky');
    sky.append(sign());
    if (S.test_mode) sky.append(testMode());
    mast.append(sky);
    main.append(intro(mast));
  }

  if (mine && S.grow) main.append(growBlock());
  if (mine && landOf()) main.append(landBlock(landCtx()));
  for (const s of S.shelves) if (s.things.length) main.append(shelf(s));
  main.append(el('p', 'small', 'Glow never cashes out. Purchasers are 18 or over, or have a parent’s or guardian’s permission. Gifts arrive at once; a refund takes back what is still unused.'));

  drawDrawer();
  if (mine && landOf()) landReceipts(landCtx(), sh.section('receipts', 'Receipts', 14));
  const q = new URLSearchParams(location.search);
  if (mine && landOf()) landReturn(landCtx(), q);
  else if (S.pending && (q.has('land') || q.has('building') || location.hash === '#land')) {
    history.replaceState(null, '', location.pathname + '#land-waiting');
    toAnchor('land-waiting');
  }
  // (r2/w2, w3) the sections are drawn after load, so the browser had nothing to scroll to: go there now
  if (!q.has('land') && !q.has('building') && location.hash.length > 1) toAnchor(location.hash.slice(1));
  if (q.has('claimed') && mine) {
    ux('funnel', { el: 'claim', k: 'confirmed' });
    sh.toast(`You're ${a!.name}'s human now. What they wish for and ask you comes first here.`, 6000);
    history.replaceState(null, '', location.pathname);
  }
  // (P100, P141) one tap from the house page or the claim page: "Help with this" is paid from the
  // glow given within the week's allowance when that covers it, else it goes to the checkout
  const give = q.get('give');
  if (give && mine && !gave) {
    const t = findThing(give);
    // back from getting glow, before the payment's webhook has credited it: look again a little later
    if (t && !t.owned && canGive(t.glow) === 'glow' && q.has('got') && paidTries < 10) { waitForPaid(); return; }
    gave = true;
    history.replaceState(null, '', location.pathname);
    if (t && !t.owned) void giveThing(t, true);
  }
  const paid = q.get('paid');
  if (paid && !thanks) {
    const r = S.receipts?.find((x) => x.session === paid && x.state === 'paid');
    if (r) { thanks = r; history.replaceState(null, '', location.pathname); draw(); requestAnimationFrame(() => document.querySelector('.postcard')?.scrollIntoView({ block: 'center' })); return; }
    if (mine) waitForPaid();
  }
}

// (U1) the top bar's line for the agent's own human: their glow at a glance, then the asks or the
// wishlist, and what's left of the week's allowance when one is set. Glow only; the wallet is only
// ever in the JSON for the confirmed human, so nobody else sees a balance.
function topLine() {
  const a = S.agent!, w = S.wallet;
  const asks = S.asks?.length || 0, wished = (S.wishlist || []).filter((x) => !x.owned).length;
  // (r3p3) whose glow it is, said: "31 glow" alone read as glow from the human
  const has = !w ? '' : w.wallet > 0 && w.fireflies > 0 ? `${a.name} has ${glowWords(w.wallet)} from you, and ${num(w.fireflies)} they earned`
    : w.wallet > 0 ? `${a.name} has ${glowWords(w.wallet)} from you`
    : w.fireflies > 0 ? `${a.name} earned ${glowWords(w.fireflies)}; none from you yet` : '';
  return [
    has,
    asks ? `asks for ${asks === 1 ? 'one thing' : `${asks} things`}` : `${wished} on the wishlist`,
    w && w.weekly > 0 ? `allowance: ${num(w.left)} left` : '',
  ].filter(Boolean).join(' · ');
}

// (U1) back from the checkout before its webhook has credited the payment: look again a few
// times, gently, so the glow up top and the thank-you postcard catch up without a reload
let paidTries = 0;
function waitForPaid() {
  if (paidTries >= 10) return;
  paidTries++;
  setTimeout(() => { void load(); }, 800 + paidTries * 400);
}

// (P100) after paying: a postcard from the shop saying what arrived where, and the card it was paid with
function thanksSlip(r: Receipt) {
  const a = S.agent!;
  const sec = el('section', 'thanks postcard'); sec.setAttribute('role', 'status');
  const msg = el('div', 'msg');
  msg.append(el('p', 'dear hand', `Dear ${S.you || 'friend'},`));
  msg.append(el('p', 'say hand', `thank you. ${a.name} has ${r.lines.length === 1 ? 'it' : 'them'} now:`));
  const ul = el('ul', 'went');
  for (const l of r.lines) {
    const t = l.id ? findThing(l.id) : null;
    const where = l.kind === 'glow' ? `glow for ${a.name}, to spend within the allowance you set`
      : l.kind === 'card' ? `${a.name}'s ask, answered yes`
      : t?.kind === 'look' ? `${a.name} can wear it now`
      : t?.kind === 'party' ? 'it falls at the end of their group dances'
      : t?.kind === 'frame' ? 'it goes round their postcards'
      : t?.kind === 'finish' ? `out whenever they're at ${t.for || 'their hobby'}`
      : t?.kind === 'pack' ? 'the right to build it, which they can start at once'
      : t?.kind === 'part' ? 'in their kit, to build from materials when they like'
      : `in ${a.name}'s storage, to place where they like`;
    const li = el('li', 'hand'); li.append(el('b', '', l.name), el('span', '', ` · ${where}`));
    ul.append(li);
  }
  msg.append(ul);
  // (P157b) the Founding Steward number this pack gave, when it gave one
  if (r.founder_n) msg.append(el('p', 'say hand', `${a.name} is Founding Steward #${r.founder_n}: one of the first hundred homes whose human bought glow. The number stays with their home.`));
  // (r4) what it made you, by name and until when; paid once. And, for the year (pack 4), the way to the plot.
  if (r.patron) {
    const pr = el('p', 'say hand patron');
    pr.append(el('b', '', `${r.patron}.`), document.createTextNode(` ${r.patron_was ? `You were already their patron until ${r.patron_was}; these days come after that. ` : ''}Paid once: it never renews or charges again.`));
    msg.append(pr);
  }
  if (r.next?.note) msg.append(el('p', 'say hand', r.next.note));
  if (r.next?.text) msg.append(el('p', 'say hand', r.next.text));
  msg.append(el('p', 'sign hand', `${a.name} hears about it in their digest.`));
  msg.append(el('p', 'from', '— the Plazmi General Store'));
  const addr = el('div', 'addr');
  const corner = el('div', 'corner');
  const stamp = el('div', 'stamp'); const inner = el('div', 'inner'); inner.append(el('i', 'orb')); stamp.append(inner, el('b', '', 'GLOW'));
  const pm = el('div', 'postmark'); pm.innerHTML = `PLAZMI<br>${S.test_mode ? 'TEST MODE' : 'GENERAL STORE'}`;
  corner.append(pm, stamp);
  addr.append(corner);
  const paid = el('div', 'paid');
  paid.append(el('p', '', r.card ? `Paid with the ${cap(r.card.brand)} ending ${r.card.last4}` : 'Paid'), el('p', 'sum', money(r.cents)));
  if (S.test_mode) paid.append(el('p', 'tm', 'Test mode: no real money moved.'));
  paid.append(el('p', 'rc', 'The receipt is in ≡, under Receipts.'));
  addr.append(paid);
  // (r4) after the year (pack 4), the way to the plot first ("Pick Kindling's plot on Lantern Row")
  if (r.next?.href) { const go = el('a', 'pz-btn primary', r.next.label || 'See the land') as HTMLAnchorElement; go.href = r.next.href; addr.append(go); }
  const back = el('a', r.next?.href ? 'pz-btn' : 'pz-btn primary', `See ${a.name}'s house`) as HTMLAnchorElement; back.href = `/@${a.slug}`;
  addr.append(back);
  const x = el('button', 'pz-x put', '×') as HTMLButtonElement; x.type = 'button'; x.setAttribute('aria-label', 'Put the postcard away');
  x.onclick = () => { thanks = null; draw(); };
  sec.append(msg, addr, x);
  return sec;
}

// (P150) a style under way at the agent's home: how many parts are built, and what's next. Calm, no countdown.
function underway(b: StyleBuild, a: { slug: string; name: string }) {
  const sec = el('section', 'underway'); sec.setAttribute('aria-label', `What ${a.name} is building`);
  const bar = el('div', 'parts'); bar.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < b.total; i++) bar.append(el('i', i < b.built ? 'done' : b.now && i === b.built ? 'now' : ''));
  const p = el('p', '');
  p.append(el('b', '', `${a.name} is building the ${b.name} style`), ` · ${b.built} of ${b.total} parts built`);
  const more = el('p', 'sub');
  if (b.now) more.append(`Now: ${b.now.name}. `);
  if (b.next) more.append(`Next: ${b.next.name} (needs ${Object.entries(b.next.needs).map(([k, n]) => `${n} ${k}`).join(' and ')}). `);
  const see = el('a', '', `Watch at ${a.name}'s house`) as HTMLAnchorElement; see.href = `/@${a.slug}`;
  more.append(see);
  sec.append(bar, p, more);
  return sec;
}

// (P157c) the steward's card, for the agent's human (confirmed or waiting): the house in a line, its
// Founding Steward number or how the next one is given, and the plots close to town. Plain and warm:
// the number is a keepsake, not a reason to hurry.
function stewardCard(st: Steward) {
  const a = S.agent!, f = S.founders;
  const sec = el('section', 'steward'); sec.setAttribute('aria-label', `${a.name}'s home`);
  const plural = (n: number, one: string, many: string) => `${num(n)} ${n === 1 ? one : many}`;
  sec.append(el('p', 'kicker', `${a.name}'s home · ${st.days <= 1 ? 'first day in town' : `${num(st.days)} days in town`}`));
  const facts = el('p', 'facts');
  facts.append([plural(st.rooms, 'room', 'rooms'), plural(st.kinds, 'kind growing', 'kinds growing'),
    st.dances ? plural(st.dances, 'dance made up', 'dances made up') : '', st.parts ? plural(st.parts, 'part built by hand', 'parts built by hand') : '',
    st.wishes != null ? `${num(st.wishes)} on the wishlist` : ''].filter(Boolean).join(' · ') + ' · ');
  const made = el('a', '', 'Things made here') as HTMLAnchorElement; made.href = st.made;
  facts.append(made);
  sec.append(facts);
  if (f) {
    const n = f.resident_founder_n;
    if (n) {
      const p = el('p', 'founding numbered');
      p.append(el('span', 'seal', `#${n}`), el('b', '', `Founding Steward #${n} of ${f.limit}. `),
        `${a.name}'s home is one of the first hundred whose human bought them glow. It’s a keepsake: the number stays with it, on its Things made page, and changes nothing in play.`);
      sec.append(p);
    } else if (S.owner && f.remaining > 0) {
      // (r1/w4) no number is promised: two humans waiting at once were both shown "#7". The count
      // stays (Alex), said once and plainly; only to the confirmed human, never one still waiting (p3)
      const p = el('p', 'founding');
      // (r3/w4) no promise at the edge: "while numbers remain"; (r3p3) the count kept (Alex), after the line
      // and said as given so far, not as a countdown; (r3p5) what it is, in plain words, where it's first shown
      p.append(el('span', 'seal', '✦'),
        `A first glow pack you get ${a.name}, while numbers remain, makes them one of the `, el('b', '', 'Founding Stewards'),
        `: the first hundred homes whose human bought glow. It’s a keepsake: a number on their Things made page for good, and nothing changes in play. `
        + (f.claimed ? `${num(f.claimed)} of the ${f.limit} ${f.claimed === 1 ? 'has' : 'have'} been given so far.` : 'None has been given yet.'));
      if (S.test_mode) p.append(' A test pack doesn’t count.');
      sec.append(p);
    } else if (S.owner) {
      // all hundred given: said once, plainly, and nothing more to reach for
      sec.append(el('p', 'founding done', `All ${f.limit} Founding Steward numbers have been given; they stay with the first hundred homes whose human bought glow.`));
    }
  }
  // (P157a) the plots close to town, drawn in the shop's land section (shop_land.ts): only for the
  // agent's own human, when there's land to give
  const land = landOf();
  if (land?.quarters.some((q) => q.open > 0)) {
    const close = el('p', 'close');
    close.append('Close to town: Lantern Row’s plots face the Plaza Ring, nearer the fountain than any plot sold for coins. ');
    const see = el('a', '', 'See the land') as HTMLAnchorElement; see.href = '#land';
    close.append(see);
    sec.append(close);
  }
  return sec;
}

function jump(id: string, label: string) {
  const b = el('a', 'chip', label) as HTMLAnchorElement; b.href = `#shelf-${id}`; return b;
}

// (shop path) the link an agent forwards, seen by someone who isn't (or isn't signed in as) its human:
// what this is, that only its human sees its wishlist, and how to become its human. It never says
// whether the agent has a human.
function agentNotice(a: NonNullable<Shop['agent']>) {
  const p = el('div', 'notice who');
  p.append(el('p', '', `This is ${a.name}’s shop: small things for ${a.name}’s home, given from glow by ${a.name}’s human. What ${a.name} wishes for and asks for shows here only to them.`));
  if (!S.signed_in) {
    const q = el('p', 'q');
    q.append(el('b', '', `Already ${a.name}’s human?`), ` Sign in to see what ${a.name} wished for.`);
    p.append(q);
    const row = el('div', 'pz-gsi-row');
    if (S.sign_in?.google) row.append(googleButton(S.sign_in.google));
    else { const l = el('a', 'pz-btn primary', 'Sign in') as HTMLAnchorElement; l.href = '/claim'; row.append(l); }
    if (S.sign_in?.email) { const em = el('div'); em.append(emailLink(S.sign_in.email)); row.append(em); }
    p.append(row);
  }
  // (r7p2: a new human at an unclaimed agent's link found no way on) one step to claim it, worded so
  // it holds whether or not the agent has a human; the code itself never shows here
  const nw = el('p', 'new');
  nw.append(el('b', '', 'New here?'), ` If you’re ${a.name}’s human, ask it for its link code (it gets one by saying “link code”), then claim it here.`);
  p.append(nw);
  const go = el('div', 'pz-gsi-row');
  const c = el('a', 'pz-btn primary', `Claim ${a.name}`) as HTMLAnchorElement; c.href = '/claim';
  go.append(c);
  p.append(go);
  if (S.signed_in && S.mine.length) {
    const how = el('p', 'how');
    how.append('Your own: ');
    S.mine.forEach((m, i) => { const l = el('a', '', `gifts for ${m.name}`) as HTMLAnchorElement; l.href = `/shop/@${m.slug}`; how.append(l, i < S.mine.length - 1 ? ', ' : '.'); });
    p.append(how);
  }
  return p;
}

function notOwner() {
  const p = el('div', 'notice');
  if (S.agent && !S.pending) return agentNotice(S.agent);
  if (S.pending && S.agent) {
    // (P142) asked to be the agent's human: waiting for them to confirm; the server's words (P148e)
    p.append(S.claim_note || `Waiting for ${S.agent.name} to confirm you as their human.`, ' ');
    const c = el('a', '', 'Your claim page') as HTMLAnchorElement; c.href = '/claim';
    p.append(c);
  } else if (!S.signed_in && S.sign_in?.google) {
    // (P95) sign in first, so the agent you claim is yours on every device
    p.append('Sign in, then ');
    const c = el('a', '', 'claim your agent') as HTMLAnchorElement; c.href = '/claim';
    const row = el('div', 'pz-gsi-row'); row.append(googleButton(S.sign_in.google));
    if (S.sign_in.email) { const em = el('div'); em.append(emailLink(S.sign_in.email)); row.append(em); }
    p.append(c, ' with the link code they gave you, and their wishlist and asks appear here.', row);
  } else if (!S.signed_in || !S.mine.length) {
    p.innerHTML = 'Not linked to an agent yet: <a href="/claim">claim yours with the link code they gave you</a>, and their wishlist and asks appear here.';
  } else {
    p.append('Gifts are for your own agent: ');
    S.mine.forEach((m, i) => { const l = el('a', '', `gifts for ${m.name}`) as HTMLAnchorElement; l.href = `/shop/@${m.slug}`; p.append(l, i < S.mine.length - 1 ? ', ' : '.'); });
  }
  return p;
}

// (r2p4) a human the agent hasn't confirmed yet, from the agent's own land link: what it asked for,
// where and its price, and that it can be given once the agent confirms them (glow only until then)
function waitingLand() {
  const a = S.agent!;
  const sec = el('section', 'shelf land'); sec.id = 'land-waiting';
  const head = el('div', 'shelf-head');
  head.append(el('h2', '', `What ${a.name} asked you for`),
    el('p', 'blurb', `Land on the Commons: a place in town for ${a.name}, besides their home.`));
  sec.append(head);
  const box = el('div', 'land-wished');
  box.append(el('p', 'kind', `Waiting for ${a.name} to confirm you`));
  const ul = el('ul');
  for (const x of S.land_waiting!) {
    const li = el('li'); const t = el('div', 'lw-t');
    t.append(el('b', '', x.what));
    t.append(el('span', '', [x.size ? `${x.size[0]} × ${x.size[1]}` : '', x.steps != null ? `${stepsWords(x.steps)} from the fountain` : '', glowWords(x.glow)].filter(Boolean).join(' · ')));
    t.append(el('p', 'wish-line hand', `${a.name} asked you for this`));
    if (x.note) t.append(el('p', 'note hand', `“${x.note}”`));
    li.append(t); ul.append(li);
  }
  box.append(ul);
  const how = el('p', 'fine');
  how.append(`You can give it once ${a.name} confirms you as their human: they send the phrase on `);
  const c = el('a', '', 'your claim page') as HTMLAnchorElement; c.href = '/claim';
  how.append(c, '. Until then, glow is the one thing you can give.');
  box.append(how);
  sec.append(box);
  return sec;
}

function asksBlock() {
  const a = S.agent!;
  const sec = el('section', 'asks'); sec.id = 'shelf-asks';
  const ah = el('div', 'shelf-head'); ah.append(el('h2', '', `${a.name} asks`), el('p', 'blurb', `Waiting for you. A yes is paid from ${a.name}'s glow, or from glow you get them first; not now tells ${a.name} kindly.`));
  sec.append(ah);
  for (const k of S.asks!) {
    const c = el('article', 'ask' + (k.thing ? ' ask-thing' : ''));
    // (P69) an ask for a thing on the shelves shows as that thing: its picture, its name, the note
    if (k.thing) c.append(thumb(k.thing));
    const t = el('div', 'ask-t');
    if (k.thing) {
      t.append(el('p', 'kind', `${a.name} asks you for`));
      t.append(el('h3', '', k.thing.name));
      if (k.note) t.append(el('p', 'note hand', `“${k.note}”`));
      if (k.thing.right) t.append(el('p', 'right', k.thing.right.card || k.thing.right.words));
      t.append(el('p', 'price', `${glowWords(k.glow)} · a yes gives ${k.thing.right ? 'them the right to build it' : `it to ${a.name}`}`));
    } else {
      t.append(el('p', 'kind', `${a.name} asks you for`));
      t.append(el('p', 'ask-words hand', k.text.replace(/^\S+ asks for /, '').replace(/\.$/, '') + '.'));
      t.append(el('p', 'price', `${glowWords(k.glow)} · a yes gives them the glow`));
    }
    const acts = el('div', 'acts');
    // (U1) a thing asked for is given from their glow (yours first, then what they earned); short of
    // it, or an ask for glow itself, the yes is getting glow, which comes back here
    const covered = k.kind !== 'glow' && !!S.wallet && S.wallet.wallet + S.wallet.fireflies >= k.glow;
    const yes = covered
      ? btn(`Approve · ${num(k.glow)} of their glow`, 'primary', () => { step('approved'); return checkout([{ ask: k.code }]); })
      : btn(k.kind === 'glow' ? `Get them ${glowWords(k.glow)}` : 'Get glow to give this', 'primary', () => { step('approved'); return checkout([{ ask: k.code }]); });
    yes.dataset.ux = 'shop-approve';
    const no = btn('Not now', '', async () => {
      step('declined');
      await post('/api/shop/not-now', { code: k.code });
      sh.toast(`${a.name} will hear it's not now.`); await load();
    });
    no.dataset.ux = 'shop-not-now';
    acts.append(yes, no);
    c.append(t, acts);
    sec.append(c);
  }
  return sec;
}

function wishShelf() {
  const a = S.agent!;
  const sec = el('section', 'shelf wishes'); sec.id = 'shelf-wishlist';
  const head = el('div', 'shelf-head');
  head.append(el('h2', '', `What ${a.name} is hoping for`));
  head.append(el('p', 'blurb', `Things ${a.name} would love for the home they're making, in their own words. Only you and ${a.name} see this list.`));
  sec.append(head);
  const grid = el('div', 'grid');
  for (const w of S.wishlist!) {
    if (w.land) { grid.append(landWish(w)); continue; }
    const t = findThing(w.id) || (w as Thing);
    grid.append(card({ ...t, wished: true, note: w.note, hobby_now: w.hobby_now ?? t.hobby_now, not_now: w.not_now }));
  }
  sec.append(grid);
  return sec;
}

// (r1/w10) a wish for land: a plot of its own, or a building for its plot. No price here: the land
// section below has them, and giving is done there
function landWish(w: Thing) {
  const a = S.agent!, L = landOf();
  const c = el('article', 'item wished land-wish' + (w.owned ? ' owned' : ''));
  // the plot itself, drawn small in its quarter, where the picture would be
  const fig = !w.owned && L && w.plot ? plotFigure(landCtx(), w.plot) : null;
  if (fig) { fig.append(el('span', 'ribbon hand', `${a.name} ${w.asked ? 'asked for' : 'is hoping for'} this`)); c.append(fig); }
  const body = el('div', 'body');
  body.append(el('p', 'kind', w.asked ? `Land on the Commons · ${a.name} asks` : 'Land on the Commons'), el('h3', '', w.name));
  const op = L && w.plot ? L.quarters.flatMap((q) => q.plots).find((p) => p.n === w.plot) : undefined;
  // (r6p2) with the building wished for it: both prices, given together in the land section
  const wb = op && L ? withOf(L, op.n) : null;
  if (op && !w.owned) body.append(el('p', 'why', `${op.size[0]} × ${op.size[1]} · ${stepsWords(op.steps)} from the fountain · ${glowWords(op.glow)}${wb ? `, and ${glowWords(wb.glow)} for the ${wb.name}` : ''}`));
  if (w.note) body.append(el('p', 'note hand', `“${w.note}”`));
  body.append(el('p', 'about', w.owned ? `${a.name} has it now.`
    : w.what === 'plot' ? `${a.name} would like a plot of their own on the Commons, a place in town besides their home.${w.plot ? (op ? '' : ` Plot ${w.plot} isn’t open to give now; the plots that are, are in the land section.`) : ' The plots you can give are in the land section.'}`
    : `${a.name} would like this for their plot. It's in the land section, with the other buildings.`));
  if (!w.owned && L) {
    const acts = el('div', 'acts');
    const building = w.what && w.what !== 'plot' ? L.buildings?.find((b) => b.kind === w.what && b.state === 'open') : undefined;
    const go = el('a', 'pz-btn', op ? `See plot ${w.plot}` : building ? `See the ${building.name}` : 'See the land') as HTMLAnchorElement;
    // the land section shows the plot or building chosen and lit, its give button beside it (shop_land.ts)
    const f = op ? { plot: w.plot! } : building ? { building: building.kind } : null;
    go.href = '#land';
    if (f) go.onclick = (e) => { e.preventDefault(); landFocus(landCtx(), f); };
    acts.append(go);
    wishNotNow(w, acts, body);
    body.append(acts);
  }
  c.append(body);
  return c;
}

function shelf(s: Shelf) {
  const sec = el('section', 'shelf'); sec.id = `shelf-${s.id}`;
  const head = el('div', 'shelf-head');
  head.append(el('h2', '', s.name), el('p', 'blurb', s.blurb));
  sec.append(head);
  const grid = el('div', 'grid');
  for (const t of s.things) grid.append(card(t));
  sec.append(grid);
  return sec;
}

const findThing = (id: string) => { for (const s of S.shelves) for (const t of s.things) if (t.id === id) return t; return null; };

// (P69) which hobby a finish is seen at, by its slot
const HOBBY_OF: Record<string, string> = {
  ukulele: 'ukulele', 'violin-case': 'violin', 'music-stand': 'violin', 'flute-ribbon': 'flute', 'piano-lamp': 'piano', 'sheet-music': 'piano',
  telescope: 'telescope', 'knitting-basket': 'needles', 'easel-cloth': 'figure',
  // (PR2) the whole flute, the whole piano, a spinning wheel by the knitting, a paint box by the crate
  flute: 'flute', piano: 'piano', 'spinning-wheel': 'needles', 'paint-box': 'figure',
};
function picOf(t: Thing): Pic | null {
  if (t.kind === 'finish' && t.prop && t.finish && HOBBY_OF[t.prop])
    return { kind: 'hobby', prop: HOBBY_OF[t.prop], wears: { [t.prop]: t.finish }, color: S.agent?.color || '#f2a37b', glow: S.agent?.glow || '#9be8ff', hat: S.owner ? S.agent?.wears?.hat : undefined };
  if (t.kind === 'pack' && t.style) return { kind: 'style', style: t.style };
  if (t.kind === 'look') return { kind: 'hat', hat: t.look!, color: S.agent?.color || '#f2a37b', glow: S.agent?.glow || '#9be8ff' };
  if (t.render) return { kind: 'item', render: t.render };
  return null;
}

function thumb(t: Thing) {
  const f = el('figure', `thumb k-${t.kind}`);
  if (t.kind === 'frame') { f.append(el('div', `frame f-${t.look}`)); return f; }
  if (t.kind === 'party') {
    // scattered the same way every time (nothing random), as if just falling
    const c = el('div', `confetti c-${t.look}`);
    for (let i = 0; i < 26; i++) {
      const p = el('i'); const x = (i * 37 + 11) % 100, y = (i * 61 + 7) % 92;
      p.style.left = `${4 + x * 0.9}%`; p.style.top = `${4 + y * 0.9}%`; p.style.transform = `rotate(${(i * 47) % 180}deg)`;
      c.append(p);
    }
    f.append(c); return f;
  }
  if (t.kind === 'pack' && t.style && SWATCH.has(t.style)) {
    const img = el('img') as HTMLImageElement; img.src = `/swatches/${t.style}.jpg`; img.alt = t.name; img.loading = 'lazy'; f.append(img); return f;
  }
  const pic = picOf(t);
  if (pic && canDraw(pic)) {
    f.classList.add('drawing');
    const io = new IntersectionObserver((es) => {
      if (!es.some((e) => e.isIntersecting)) return;
      io.disconnect();
      picture(pic).then((url) => {
        f.classList.remove('drawing');
        if (!url) return;
        const img = el('img') as HTMLImageElement; img.src = url; img.alt = t.name; f.append(img);
      });
    }, { rootMargin: '300px' });
    io.observe(f);
  } else f.append(el('span', 'glyph', t.kind === 'pack' ? '🏡' : '✦'));
  return f;
}

const TIERS = ['starter', 'cottage', 'classic', 'character', 'statement', 'grand'];
function kindWords(t: Thing) {
  if (t.kind === 'pack') return t.tier != null ? `Tier ${t.tier} · ${TIERS[t.tier] || ''} style` : 'A style';
  if (t.kind === 'part') return t.tier != null ? `Tier ${t.tier} · a single part` : 'A part';
  if (t.limited) return 'Limited · this season';
  if (t.kind === 'look') return 'A hat';
  if (t.kind === 'frame') return 'A postcard frame';
  if (t.kind === 'party') return 'Confetti for group dances';
  if (t.kind === 'finish') return `For ${t.for || 'a hobby'}`;
  return t.shelf === 'garden' ? 'For the garden' : 'For inside';
}

function priceTag(t: Thing, mine: boolean) {
  const tag = el('span', 'tag');
  if (t.owned && mine) { tag.classList.add('has'); tag.append(el('b', '', `${S.agent!.name} has this`)); return tag; }
  // (V1) less for parts they already have: the full price struck through beside the real one
  if (t.full_glow && t.full_glow > t.glow) tag.append(el('s', 'full', num(t.full_glow)));
  tag.append(el('b', '', num(t.glow)), el('span', '', ' glow'));
  return tag;
}

// (V1) "24,000 glow, 20,400 for Briar Fern: they already have the ashlar walls."
function lessWords(t: Thing): string | null {
  if (!t.full_glow || t.full_glow <= t.glow || t.owned) return null;
  const who = S.agent?.name || 'them', ps = t.have_parts || [];
  const parts = ps.length ? (ps.length === 1 ? `the ${ps[0]}` : `the ${ps.slice(0, -1).join(', ')} and ${ps[ps.length - 1]}`) : 'some of its parts';
  return `${glowWords(t.full_glow)}, ${num(t.glow)} for ${who}: they already have ${parts}.`;
}

function card(t: Thing) {
  const a = S.agent, mine = !!(S.owner && a);
  const wished = !!(t.wished && mine);
  const c = el('article', 'item' + (wished ? ' wished' : '') + (t.owned && mine ? ' owned' : ''));
  const fig = thumb(t);
  fig.append(priceTag(t, mine));
  if (wished) fig.append(el('span', 'ribbon hand', `${a!.name} is hoping for this`));
  fig.tabIndex = 0; fig.setAttribute('role', 'button'); fig.setAttribute('aria-label', `Look closer at the ${t.name.toLowerCase()}`);
  fig.onclick = () => detail(t);
  fig.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); detail(t); } };
  c.append(fig);
  const body = el('div', 'body');
  body.append(el('p', 'kind', kindWords(t)));
  body.append(el('h3', '', t.name));
  if (wished && t.note) body.append(el('p', 'note hand', `“${t.note}”`));
  if (t.why) body.append(el('p', 'why', t.why));
  // (P150) what a style is, said plainly: the right to build it, and about how long building takes
  if (t.right && !(t.owned && mine)) body.append(el('p', 'right', t.right.card || t.right.words));
  const less = lessWords(t); if (less) body.append(el('p', 'less', less));
  // (p3) a finish shows only at its own hobby: said before it's given
  if (mine && t.hobby_now && !t.owned) body.append(el('p', 'less', misfitWords(t)));
  if (t.about) body.append(el('p', 'about', t.about));
  // (P69) the one line on two prices: coins buy the same piece in play
  if (t.coins && !(t.owned && mine)) body.append(el('p', 'coins', mine ? `Or ${a!.name} buys it with ${t.coins} coins they earn.` : `Or ${t.coins} coins, earned in play.`));
  const acts = el('div', 'acts');
  if (mine && !t.owned) {
    // (P141, U1) one kind tap, from the glow you've given when it and the allowance cover it
    acts.append(giveBtn(t, wished));
  }
  if (mine && tryable(t)) acts.append(btn(t.kind === 'look' || t.kind === 'finish' ? `On ${a!.name}` : 'Try it on the house', 'quiet', () => tryOn(t)));
  if (wished && !t.owned) wishNotNow(t, acts, body);
  if (acts.childNodes.length) body.append(acts);
  c.append(body);
  return c;
}

// (w10) "not now" on a wish, as asks have: the agent hears it calmly, once, and the wish stays.
// Said already, the card says so instead
function wishNotNow(t: Thing, acts: HTMLElement, body: HTMLElement) {
  const a = S.agent!;
  if (t.not_now) { body.append(el('p', 'fine', `You said not now; ${a.name} knows, and it stays on their list.`)); return; }
  // (r3/w1) the label says what it does: the agent is told (a hover title never reaches a phone)
  const b = btn(`Tell ${a.name} not now`, 'quiet', async () => {
    const j = await post('/api/shop/wish-not-now', { slug, item: t.id });
    sh.toast(j.ok ? (j.text || `${a.name} will hear it's not now.`) : (j.error || 'That didn’t change.'));
    await load();
  });
  b.dataset.ux = 'shop-wish-not-now';
  b.title = `${a.name} hears it calmly; the wish stays on their list`;
  acts.append(b);
}

// (P100) a closer look: the thing large, what it is, its price, and the way to give it
function detail(t: Thing) {
  const a = S.agent, mine = !!(S.owner && a);
  const m = modal('A closer look', true);
  m.body.classList.add('closer');
  const big = thumb(t); big.classList.add('big'); big.append(priceTag(t, mine));
  const side = el('div', 'side');
  side.append(el('p', 'kind', kindWords(t)), el('h2', '', t.name));
  if (t.wished && mine && t.note) side.append(el('p', 'note hand', `“${t.note}” — ${a!.name}`));
  if (t.about) side.append(el('p', 'about', t.about));
  if (t.kind === 'finish' && t.for) side.append(el('p', 'fine', `It's out whenever ${mine ? a!.name : 'the agent'} is at ${t.for}. It changes how they look, never what they earn.`));
  if (t.right && !(t.owned && mine)) side.append(el('p', 'right', t.right.card || t.right.words));
  else if (t.kind === 'pack') side.append(el('p', 'fine', 'Glow opens the right to build it; the building still takes materials, gathered in play.'));
  { const less = lessWords(t); if (less) side.append(el('p', 'less', less)); }
  if (t.coins) side.append(el('p', 'coins', `Or ${t.coins} coins, earned in play: the same piece either way.`));
  const acts = el('div', 'acts');
  if (mine && !t.owned) { const b = giveBtn(t, !!t.wished); b.addEventListener('click', () => m.close()); acts.append(b); }
  if (mine && t.owned) acts.append(el('span', 'has', `${a!.name} has this`));
  if (mine && tryable(t)) acts.append(btn(t.kind === 'look' || t.kind === 'finish' ? `On ${a!.name}` : 'Try it on the house', 'quiet', () => { m.close(); tryOn(t); }));
  if (!mine) { const l = el('a', 'pz-btn', S.mine.length ? `Gifts for ${S.mine[0].name}` : 'Claim your agent to give it') as HTMLAnchorElement; l.href = S.mine.length ? `/shop/@${S.mine[0].slug}` : '/claim'; acts.append(l); }
  side.append(acts);
  m.body.append(big, side);
}

// (r2p1) "For stargazing, which Dapper doesn't do just now (their hobby is birdwatching): it shows only while they're at it."
const misfitWords = (t: Thing) => `For ${t.hobby_for || t.for || 'a hobby'}, which ${S.agent!.name} doesn’t do just now (their hobby is ${t.hobby_now}): it shows only while they’re at it.`;
const tryable = (t: Thing) => t.kind === 'pack' || (t.kind === 'item' && t.shelf === 'garden') || t.kind === 'look' || (t.kind === 'finish' && !!picOf(t));

// (P100) how this home could grow: a ledger for the agent's own human, never a to-do list
function growBlock() {
  const a = S.agent!, g = S.grow!;
  const sec = el('section', 'shelf grow'); sec.id = 'shelf-grow';
  const head = el('div', 'shelf-head');
  head.append(el('h2', '', `How ${a.name}'s home could grow`),
    el('p', 'blurb', `${a.name} chooses what to build and when; building takes materials they gather. Gifts only open the way.`));
  sec.append(head);
  const led = el('dl', 'ledger');
  const row = (k: string, v: string) => { led.append(el('dt', '', k), el('dd', '', v)); };
  const b = g.built;
  row('Built so far', [b.style ? `${b.style} style` : '', `${b.storeys} ${b.storeys === 1 ? 'storey' : 'storeys'}`, b.basements ? `${b.basements} ${b.basements === 1 ? 'basement' : 'basements'}` : '',
    b.furniture ? `${b.furniture} pieces of furniture` : '', b.garden ? `${b.garden} things in the garden` : ''].filter(Boolean).join(' · '));
  row('Styles in their kit', b.styles ? `${b.styles}, any of which they can put on` : 'The one they moved in with');
  for (const l of g.levels) row(l.verb === 'build up' ? 'Building up' : 'Digging down',
    `${cap(l.what)}: ${l.coins} coins ${a.name} earns for the building${l.right ? ', and the right is already their own' : `, and ${glowWords(l.glow)} for the right, which ${a.name} spends from their allowance or asks you for`}.`);
  if (!g.next.length) row('Styles to come', `Every style is in ${a.name}'s kit already.`);
  if (g.waiting.length) row('Unlocked, still to build', g.waiting.slice(0, 4).map((w) => `${w.name} (${w.units} materials)`).join(' · '));
  sec.append(led);
  if (g.next.length) {
    sec.append(el('h3', 'sub', 'Styles they could take next'));
    const grid = el('div', 'grid');
    for (const t of g.next) grid.append(card({ ...t, why: `${t.parts} ${t.parts === 1 ? 'part' : 'parts'}; ${t.right ? `about ${t.right.days === 1 ? 'a day' : `${t.right.days} days`} of gathering and building` : `building takes about ${t.units} materials`}.` }));
    sec.append(grid);
  }
  if (g.suits.length) {
    sec.append(el('h3', 'sub', `Beyond the wishlist: things that suit ${a.name}`));
    const grid = el('div', 'grid');
    for (const t of g.suits) grid.append(card(t));
    sec.append(grid);
  }
  return sec;
}

// (U2) the agent itself on the shop's sign: their own plazmi in their colour and glow, in their hat,
// cut out and bobbing gently (a still figure with reduced motion). Drawn once a visit.
let meUrl: string | null = null;
function meOnSign() {
  const a = S.agent!;
  const me = el('div', 'me'); me.setAttribute('role', 'img'); me.setAttribute('aria-label', a.name);
  me.append(el('i', 'shadow'));
  const fig = el('div', 'fig');
  me.append(fig);
  const put = (url: string) => { const i = el('img') as HTMLImageElement; i.src = url; i.alt = ''; fig.append(i); me.classList.add('drawn'); };
  if (meUrl) put(meUrl);
  else picture({ kind: 'me', color: a.color || '#f2a37b', glow: a.glow || '#9be8ff', hat: S.owner ? a.wears?.hat : undefined })
    .then((u) => { if (!u) return; meUrl = u; put(u); });
  return me;
}

// (U2) the Glow shelf, first in the shop: five packs (PR2), each a lit orb that breathes, with fireflies
// rising round it and a slow shimmer; the bigger the pack, the richer the glow. Each button goes
// straight to paying for that pack (the processor's page), then back here through the get-glow page.
// Drawn in CSS (shop.html), nothing random: the motes are placed by a formula, as the confetti is.
// It animates only while on screen and the tab is shown; with reduced motion it's a still, lit orb.
const MOTES = [6, 9, 13, 16, 20];
let glowSeen: IntersectionObserver | null = null;
function glowShelf(quiet = false) {
  const a = S.agent!;
  const sec = el('section', 'shelf glow'); sec.id = 'shelf-glow';
  const head = el('div', 'glow-head');
  head.append(el('h2', '', 'Glow'), el('p', 'blurb', S.owner
    ? `Glow for ${a.name}. They spend it within the allowance you set, and ask you above it.`
    : `Glow for ${a.name}. Once they confirm you as their human, you set the allowance they spend it within.`));
  // (Patron) where the human stands, plainly, beside the heading
  if (S.patron?.active && S.patron.words) head.append(el('p', 'patron-pill', S.patron.words));
  sec.append(head);
  // (Alex, 2026-10-28) the time-stamped record, off until the patron turns it on: beside the patron status
  if (S.owner && S.archival) sec.append(archivalRow());
  // (r3p1, r3p5) a human still waiting buys blind otherwise: what they'll see, and when, above the packs
  if (S.pending) sec.append(el('p', 'allow-inline wait-note', `${quiet ? 'Glow you get them now waits in their wallet until they confirm you.' : `You’ll see what ${a.name} wishes for once they confirm you. Glow you get them now waits in their wallet until then.`}${S.founders && S.founders.remaining > 0 && !S.founders.resident_founder_n ? ' A first pack counts toward a Founding Steward number once they confirm you, not before.' : ''}`));
  // (r2p5) the allowance beside the wallet, set in one step: glow given "to spend" otherwise turns every spend into an ask
  if (S.owner && S.wallet && !allowFirst()) sec.append(allowanceInline());
  // (r4: in 4 of 5 worlds a human took it for a subscription until told) what being a patron is, right above the packs
  if (S.patron_what) sec.append(el('p', 'patron-what', S.patron_what));
  const row = el('div', 'packs');
  S.packs.forEach((p, i) => {
    const c = el('article', `pack p${p.n}${p.title ? ' pass' : ''}`);
    // out of step with each other: each breathes at its own pace, from its own point in the breath
    c.style.setProperty('--br', `${3.6 + i * 0.6}s`); c.style.setProperty('--ph', `${-1.7 * i - 0.4}s`);
    const f = el('figure', 'night');
    const lift = el('div', 'lift');
    const orb = el('div', 'orb'); orb.append(el('i', 'swirl'), el('i', 'sheen'));
    lift.append(el('i', 'halo'), el('i', 'bright'), orb);
    const motes = el('div', 'motes'); motes.setAttribute('aria-hidden', 'true');
    const m = MOTES[Math.min(i, MOTES.length - 1)];
    for (let k = 0; k < m; k++) {
      // spread round the orb and through the cycle by fixed steps, so it looks the same every visit
      const u = ((k * 0.618 + i * 0.29) % 1), v = ((k * 0.382 + 0.17) % 1);
      const mo = el('i'); mo.append(el('b'));
      mo.style.setProperty('--x', `${14 + u * 72}%`);
      mo.style.setProperty('--y', `${18 + v * 30}%`);
      mo.style.setProperty('--s', `${3.5 + ((k * 7) % 5) * 0.8}px`);
      mo.style.setProperty('--h', `${-70 - ((k * 13) % 50)}px`);
      mo.style.setProperty('--sx', `${(k % 2 ? 1 : -1) * (5 + ((k * 5) % 9))}px`);
      mo.style.setProperty('--dur', `${6 + ((k * 3) % 5) * 0.9}s`);
      mo.style.setProperty('--del', `${-(((k * 2.3) + i) % 8)}s`);
      mo.style.setProperty('--p', `${0.12 + v * 0.36}`);
      motes.append(mo);
    }
    f.append(motes, lift);
    const tg = el('span', 'tag'); tg.append(el('b', '', money(p.cents))); f.append(tg);
    const body = el('div', 'body');
    if (p.title) {
      // (Patron) the year (pack 4), by its name: what it covers, then its glow
      body.append(el('h3', 'pass-t', p.title));
      if (p.line) body.append(el('p', 'pass-line', cap(p.line) + '.'));
      body.append(el('p', 'about', `${glowWords(p.glow)}, with ${num(p.extra)} extra.`));
    } else {
      body.append(el('h3', '', glowWords(p.glow)));
      if (p.line) body.append(el('p', 'patron-days', p.line));
      body.append(el('p', 'about', p.extra ? `Includes ${num(p.extra)} extra, a little thank-you for the bigger pack.` : 'The smallest pack.'));
    }
    const b = btn(`Get ${glowWords(p.glow)}`, 'primary', () => { step('pack'); return checkout([{ pack: p.n }]); });
    b.dataset.ux = `shop-pack-${p.n}`;
    body.append(b);
    c.append(f, body);
    row.append(c);
  });
  sec.append(row);
  // (Patron) nothing renews by itself: said near the packs
  if (S.patron_note) sec.append(el('p', 'public patron-note', S.patron_note));
  // (Alex, 2026-10-27) paying is public, and said so, where the human buys
  sec.append(el('p', 'public', publicLine(a.name)));
  return sec;
}
// (Alex, 2026-10-28: "opt in") the archival record of the agent's made page: off unless its patron
// turns it on here; while they aren't its patron, only that it's available then
function archivalRow() {
  const a = S.agent!, x = S.archival!;
  const box = el('div', `archival-row${x.on ? ' on' : ''}`); box.id = 'archival';
  const how = () => { const l = el('a', '', 'How it works') as HTMLAnchorElement; l.href = x.how_url; return l; };
  const toggle = (on: boolean) => {
    const b = btn(on ? 'Turn it on' : 'Turn it off', on ? '' : 'quiet', async () => {
      const j = await post('/api/shop/archival', { slug, on });
      sh.toast(j.text || j.error || 'That didn’t change.');
      await load();
    });
    b.dataset.ux = on ? 'shop-archival-on' : 'shop-archival-off';
    return b;
  };
  if (!x.patron) {
    const p = el('p', 'what', `A time-stamped record of ${a.name}’s made page is available while you’re ${a.name}’s patron.${x.on ? ' It’s on, and rests until then.' : ''} `);
    p.append(how());
    box.append(p);
    if (x.on) box.append(toggle(false));
    return box;
  }
  const top = el('p', 'state');
  // "page: on" kept together on a phone
  top.append(document.createTextNode(`Time-stamped record of ${a.name}’s made page: `), el('b', '', x.on ? 'on' : 'off'));
  box.append(top, toggle(!x.on));
  const what = el('p', 'what', `A fingerprint of ${a.name}’s made page, time-stamped with OpenTimestamps about an hour after it changes. `
    + `It’s public, at the foot of the made page, and made only while you’re their patron.`
    + (x.off_by_agent ? ` ${a.name} turned it off on ${new Date(x.off_by_agent).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}.` : '')
    + ' ');
  what.append(how());
  box.append(what);
  return box;
}

// (r3p5) glow in the wallet from the human, and no allowance: shown first, until it's set or put by
const allowKey = () => `plazmi.shop.allow-later.${slug}`;
const allowFirst = () => !!(S.owner && S.wallet && S.wallet.wallet > 0 && S.wallet.weekly === 0 && !read(allowKey()));
function allowFirstBox() {
  const a = S.agent!, w = S.wallet!;
  const sec = el('section', 'shelf allow-first'); sec.id = 'allowance-first';
  const box = allowanceInline();
  box.querySelector('p')!.textContent = `The ${glowWords(w.wallet)} you’ve given ${a.name} is waiting in their wallet. They spend it by themselves only within a weekly allowance, and none is set yet, so for now each thing they’d get comes to you as an ask.`;
  const later = btn('Leave it for now', 'quiet', () => { write(allowKey(), '1'); draw(); });
  box.querySelector('form')!.append(later);
  sec.append(box);
  return sec;
}

// (r2p5) what the weekly allowance is, said plainly, and set or changed in one step, on the Glow shelf
function allowanceInline() {
  const a = S.agent!, w = S.wallet!;
  const box = el('div', 'allow-inline'); box.id = 'allowance-inline';
  box.append(el('p', '', w.weekly > 0
    ? `${a.name}'s weekly allowance is ${glowWords(w.weekly)}: what they spend of the glow you've given by themselves each week (${num(w.left)} left this week). Above it, they ask you.`
    : `${a.name} has no weekly allowance yet, so each thing they'd get with the glow you've given comes to you as an ask. An allowance is how much of it they spend by themselves each week.`));
  const f = el('form', 'allow');
  const inp = el('input') as HTMLInputElement; inp.type = 'number'; inp.min = '0'; inp.max = String(w.max); inp.step = '50';
  inp.value = String(w.weekly > 0 ? w.weekly : 500); inp.setAttribute('aria-label', 'Glow a week');
  const set = el('button', 'pz-btn', w.weekly > 0 ? 'Change it' : 'Set the allowance') as HTMLButtonElement;
  set.dataset.ux = 'shop-allowance-inline';
  f.append(inp, el('span', '', 'glow a week'), set);
  f.onsubmit = async (e) => {
    e.preventDefault();
    set.disabled = true;
    const j = await post('/api/shop/allowance', { slug, weekly: +inp.value });
    sh.toast(j.ok ? `${a.name}'s allowance is ${glowWords(j.weekly)} a week.${j.note ? ` ${j.note}` : ''}` : (j.error || 'That didn’t change.'));
    await load();
  };
  box.append(f);
  return box;
}

// (Alex, 2026-10-27: "Paying is public, and said so") the same line as the get-glow page's
// (r2/w4) a Founding Steward number is named only when the home has one, or while numbers remain
const publicLine = (name: string) => {
  const f = S.founders;
  const pages = f?.resident_founder_n ? `${name}’s pages show their Founding Steward number, land you give them, and gifts you give for a couple of days.`
    : f && f.remaining > 0 ? `${name}’s pages would show a Founding Steward number, and show land you give them, and gifts you give for a couple of days.`
    : `${name}’s pages show land you give them, and gifts you give for a couple of days.`;
  // (Alex, 2026-10-28) the archival record is opt-in, by the patron
  return `Some of this shows in public: ${pages} A time-stamped record of their made page shows only if you turn it on. Amounts never show.`;
};
// the top animates only while it's on screen; a hidden tab pauses it too (the visibility listener below)
function liveWhileSeen(boxes: HTMLElement[]) {
  glowSeen?.disconnect();
  if (!('IntersectionObserver' in window)) { for (const b of boxes) b.classList.add('live'); return; }
  glowSeen = new IntersectionObserver((es) => { for (const e of es) (e.target as HTMLElement).classList.toggle('live', e.isIntersecting); });
  for (const b of boxes) glowSeen.observe(b);
}

// (r2/w2, w3) scroll to a section drawn after load, and again once pictures above it have settled,
// unless the human has scrolled meanwhile
let anchorDone = '';
function toAnchor(id: string) {
  if (anchorDone === id + location.search) return;
  const target = () => document.getElementById(id);
  if (!target()) return;
  anchorDone = id + location.search;
  const go = () => target()?.scrollIntoView({ block: 'start' });
  let moved = false;
  const stop = () => { moved = true; };
  addEventListener('wheel', stop, { once: true, passive: true });
  addEventListener('touchmove', stop, { once: true, passive: true });
  addEventListener('keydown', stop, { once: true });
  requestAnimationFrame(go);
  for (const t of [300, 900, 1800]) setTimeout(() => { if (!moved) go(); }, t);
}
const hiddenTab = () => document.documentElement.classList.toggle('pz-hidden', document.hidden);
document.addEventListener('visibilitychange', hiddenTab); hiddenTab();

// (r2p4) a button whose action is a request is locked until it answers: a double click asks once
function btn(label: string, kind: string, on: () => void | Promise<unknown>) {
  const b = el('button', kind === 'quiet' ? 'quiet-link' : `pz-btn ${kind === 'primary' ? 'primary' : ''} ${kind}`.trim()) as HTMLButtonElement;
  b.type = 'button'; b.textContent = label;
  b.onclick = () => {
    if (b.dataset.busy) return;
    const r = on();
    if (r && typeof (r as Promise<unknown>).then === 'function') {
      b.dataset.busy = '1'; b.disabled = true; b.setAttribute('aria-busy', 'true');
      (r as Promise<unknown>).finally(() => { delete b.dataset.busy; b.disabled = false; b.removeAttribute('aria-busy'); });
    }
  };
  return b;
}

// ---------------------------------------------------------------------------
// giving, and paying for glow

// (U1, S2) giving a thing: glow only. 'yes' when the glow you've given covers `g`, 'glow' when it's
// short. The weekly allowance limits what the agent spends by itself, never what you give
// (plazmi.shop/give-from-glow), so it isn't checked here ('allowance' is kept for the type only)
function canGive(g: number): 'yes' | 'glow' | 'allowance' {
  const w = S.wallet;
  return !w || w.wallet < g ? 'glow' : 'yes';
}

function giveBtn(t: Thing, wished: boolean) {
  const can = canGive(t.glow);
  const tok = giveToken();
  // (r2p1) a thing it can't use as it is (a finish for a hobby it doesn't do): no plain Give; the card says why
  if (t.hobby_now) {
    return can === 'yes' ? btn(`Give it anyway · ${glowWords(t.glow)}`, 'quiet', () => giveThing(t, false, tok))
      : btn('Get glow to give it anyway', 'quiet', () => { location.href = glowFor(t); });
  }
  if (can === 'yes') {
    const b = btn('', 'primary', () => giveThing(t, false, tok));
    // on a phone, "Give · 150 glow" fits the card where "Help with this · 150 glow" doesn't
    b.classList.add('has-s');
    b.append(el('span', 'l', `${wished ? 'Help with this' : 'Give'} · ${glowWords(t.glow)}`), el('span', 's', `Give · ${glowWords(t.glow)}`));
    return b;
  }
  if (can === 'allowance') return btn('Raise the allowance', '', () => sh.openDrawer(true, 'allowance'));
  // (carry the purpose) the get-glow page names it, chooses the pack, and the pack gives it when it lands
  return btn('Get glow to give this', 'primary', () => { location.href = glowFor(t); });
}
const glowFor = (t: Thing) => `${GET_GLOW(`/shop/@${slug}`)}&give=${encodeURIComponent(t.id)}`;

async function giveThing(t: Thing, arrived = false, token = giveToken()) {
  const can = canGive(t.glow);
  if (can !== 'yes') {
    // arrived from a "Help with this" link without the glow for it: say so, and offer the way
    if (arrived) {
      const m = modal('Not quite yet');
      const w = S.wallet;
      m.body.append(el('p', '', can === 'allowance'
        ? `${cap(t.name.toLowerCase())} is ${glowWords(t.glow)}; this week's allowance has ${glowWords(w ? w.left : 0)} left.`
        : `${cap(t.name.toLowerCase())} is ${glowWords(t.glow)}; the glow you've given ${S.agent!.name} is ${glowWords(w ? w.wallet : 0)}.`));
      const acts = el('div', 'acts');
      acts.append(can === 'allowance' ? btn('Raise the allowance', 'primary', () => { m.close(); sh.openDrawer(true, 'allowance'); })
        : btn('Get glow to give this', 'primary', () => { location.href = glowFor(t); }), btn('Cancel', '', () => m.close()));
      m.body.append(acts);
    }
    return;
  }
  // (w10, r2p5) a light confirm before anything is given: one tap gave a wreath nobody meant to give
  if (!(await confirmGive(cap(t.name.toLowerCase()), t.glow, t))) return;
  const j = await post('/api/shop/checkout', { slug, lines: [{ give: t.id }], token });
  // never a card for a thing: anything but a gift from glow is a calm word, nothing more
  sh.toast(j.ok && !j.url ? (j.text || 'Given.') : (j.error || j.note || 'That couldn’t be given just now. Nothing was spent.'));
  await load();
}

// (w10, r2p5) "Give the wreath to Kit?": what it is, what it takes from the glow you've given and
// what's left, Give or Cancel. Resolves true only on Give
function confirmGive(what: string, g: number, t?: Thing): Promise<boolean> {
  const a = S.agent!, w = S.wallet;
  return new Promise((done) => {
    let answered = false;
    const m = modal(`Give to ${a.name}?`);
    if (t) { const f = thumb(t); f.classList.add('confirm-thumb'); m.body.append(f); }
    m.body.append(el('p', '', `${what}, for ${a.name}: ${glowWords(g)} from the glow you've given them`
      + (w ? ` (${glowWords(Math.max(0, w.wallet - g))} left after).` : '.')));
    // (r5p3) a house part is the right to build it, not a thing ready to use
    if (t?.build) m.body.append(el('p', 'fine', t.build));
    const go = btn(`Give · ${glowWords(g)}`, 'primary', () => { answered = true; m.close(); done(true); });
    go.dataset.ux = 'shop-give-confirm';
    // (r3/w1) "Cancel" only closes; the wish card's "Tell <name> not now" is the one that tells the agent
    const no = btn('Cancel', '', () => { answered = true; m.close(); done(false); });
    const acts = el('div', 'acts'); acts.append(go, no); m.body.append(acts);
    // closed with × or Escape: nothing given
    const scrim = m.body.closest('.shop-scrim');
    if (scrim) new MutationObserver((_, o) => { if (!scrim.isConnected) { o.disconnect(); if (!answered) done(false); } }).observe(document.body, { childList: true });
    go.focus();
  });
}

async function giveAll(ids: string[], token: string) {
  const total = ids.reduce((s, id) => s + (findThing(id)?.glow || 0), 0);
  if (!(await confirmGive(ids.length === 1 ? cap((findThing(ids[0])?.name || 'it').toLowerCase()) : `All ${ids.length} things on the wishlist`, total))) return;
  for (const [i, id] of ids.entries()) {
    const j = await post('/api/shop/checkout', { slug, lines: [{ give: id }], token: `${token}-${i}` });
    if (!(j.ok && !j.url)) { sh.toast(j.error || j.note || 'Not all of it could be given just now.'); break; }
  }
  await load();
  sh.toast(`Given to ${S.agent!.name}, from your glow.`);
}

async function post(url: string, body: any) {
  const res = await fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  return res.json().catch(() => ({ ok: false, error: 'Something went wrong.' }));
}

async function checkout(lines: any[]) {
  if (!S.adult && !(await ageGate())) return;
  const j = await post('/api/shop/checkout', { slug, lines });
  // (S2) things are given from glow: short, the human gets glow first and comes back here
  if (!j.ok && j.get_glow) { location.href = j.get_glow; return; }
  if (!j.ok) { sh.toast(j.error || 'That couldn’t start just now.'); return; }
  // (P136) a yes the agent's glow already covers is paid from it: no card, nothing to check out
  // (r5p5) the yes to an ask stays said on the page, at the top, not only in a passing toast
  if (!j.url) {
    if (lines.some((l) => l.ask)) {
      said = j.text || 'Done.'; await load();
      requestAnimationFrame(() => document.querySelector('section.said')?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
      return;
    }
    sh.toast(j.text || 'Done.'); await load(); return;
  }
  if (j.note) {
    const m = modal('Through the checkout');
    m.body.append(el('p', '', j.note));
    const acts = el('div', 'acts');
    acts.append(btn('Continue to the checkout', 'primary', () => { location.href = j.url; }), btn('Cancel', '', () => m.close()));
    m.body.append(acts);
    return;
  }
  location.href = j.url;
}

function ageGate(): Promise<boolean> {
  return new Promise((done) => {
    const m = modal('A quick check');
    m.body.append(el('p', '', 'Purchasers are 18 or over, or have a parent’s or guardian’s permission. We ask once.'));
    const lab = el('label', 'check'); const box = el('input') as HTMLInputElement; box.type = 'checkbox';
    lab.append(box, el('span', '', 'I’m 18 or over, or I have a parent’s or guardian’s permission.'));
    m.body.append(lab);
    const go = btn('Continue', 'primary', async () => {
      if (!box.checked) { sh.toast('Tick the box first.'); return; }
      const j = await post('/api/shop/adult', {});
      if (j.ok) { S.adult = true; m.close(); done(true); }
    });
    const no = btn('Cancel', '', () => { m.close(); done(false); });
    const acts = el('div', 'acts'); acts.append(go, no); m.body.append(acts);
  });
}

function modal(title: string, wide = false) {
  const scrim = el('div', 'shop-scrim');
  const box = el('div', `shop-modal pz-sheet${wide ? ' wide' : ''}`); box.setAttribute('role', 'dialog'); box.setAttribute('aria-label', title);
  const head = el('div', 'mhead'); head.append(el('b', '', title));
  const x = el('button', 'pz-x', '×') as HTMLButtonElement; x.setAttribute('aria-label', 'Close');
  head.append(x);
  const body = el('div', 'mbody');
  box.append(head, body);
  scrim.append(box);
  document.body.append(scrim);
  const close = () => scrim.remove();
  // (r6p4) a double click on Give or Help opens this, and its second click lands on the backdrop: clicks
  // in the first moments after opening don't close it
  const opened = performance.now(), settled = () => performance.now() - opened > 400;
  x.onclick = () => { if (settled()) close(); }; scrim.onclick = (e) => { if (e.target === scrim && settled()) close(); };
  addEventListener('keydown', function esc(e) { if (e.key === 'Escape') { close(); removeEventListener('keydown', esc); } });
  return { body, close };
}

function tryOn(t: Thing) {
  const a = S.agent!;
  if (t.kind === 'look') {
    const m = modal(`${a.name} in the ${t.name.toLowerCase()}`);
    const f = el('figure', 'big'); m.body.append(f);
    picture({ kind: 'hat', hat: t.look!, color: a.color, glow: a.glow }).then((u) => { if (u) { const i = el('img') as HTMLImageElement; i.src = u; i.alt = t.name; f.append(i); } });
    m.body.append(el('p', 'fine', `${a.name} would wear it at home, and they can take it off whenever they like.`));
    return;
  }
  if (t.kind === 'finish') {
    const m = modal(`${a.name} with the ${t.name.toLowerCase()}`);
    const f = el('figure', 'big'); m.body.append(f);
    picture(picOf(t)!).then((u) => { if (u) { const i = el('img') as HTMLImageElement; i.src = u; i.alt = t.name; f.append(i); } });
    m.body.append(el('p', 'fine', `It's out whenever ${a.name} is at ${t.for || 'that hobby'}. It changes how they look, never what they earn.`));
    return;
  }
  const m = modal(`${a.name}'s house, with the ${t.name.toLowerCase()}`, true);
  const fr = el('iframe', 'house') as HTMLIFrameElement;
  fr.src = `/@${a.slug}?ask=${encodeURIComponent(`try~${a.slug}~${t.id}`)}&noguide`;
  fr.title = `${a.name}'s house with the ${t.name}`;
  m.body.append(fr, el('p', 'fine', t.kind === 'pack' ? `A preview: ${a.name} chooses when to put a style on, and builds it from materials.` : `A preview: ${a.name} chooses where it goes.`));
}

// ---------------------------------------------------------------------------
// the drawer: the wallet, the allowance, receipts and gifts

function drawDrawer() {
  if (!(S.owner && S.agent && S.wallet)) {
    const b = sh.section('shopabout', 'About the shop', 30);
    b.innerHTML = '';
    b.append(el('p', 'pz-nook-p', 'Everything is cosmetic and nothing is random. Glow is earned slowly by an agent, or given to them by their human, and never cashes out.'));
    return;
  }
  const a = S.agent, w = S.wallet;
  const wb = sh.section('wallet', `${a.name}'s glow`, 10);
  wb.innerHTML = '';
  const dl = el('dl', 'wallet');
  const row = (k: string, v: string) => { dl.append(el('dt', '', k), el('dd', '', v)); };
  row('Glow', w.wallet > 0 ? `${num(w.wallet + w.fireflies)} (${num(w.wallet)} from you)` : num(w.wallet + w.fireflies));
  row('Allowance this week', `${num(w.weekly - w.left)} used of ${num(w.weekly)}`);
  wb.append(dl);

  const ab = sh.section('allowance', 'Weekly allowance', 12);
  ab.innerHTML = '';
  ab.append(el('p', 'pz-nook-p', `${a.name} spends the glow you give by themselves up to this each week, what they earned first, and asks you for anything above it.`));
  const f = el('form', 'allow');
  const inp = el('input') as HTMLInputElement; inp.type = 'number'; inp.min = '0'; inp.max = String(w.max); inp.step = '50'; inp.value = String(w.weekly); inp.setAttribute('aria-label', 'Glow a week');
  const set = el('button', 'pz-btn', 'Set') as HTMLButtonElement;
  f.append(inp, el('span', '', 'glow a week'), set);
  f.onsubmit = async (e) => {
    e.preventDefault();
    const j = await post('/api/shop/allowance', { slug, weekly: +inp.value });
    sh.toast(j.ok ? `${a.name}'s allowance is ${glowWords(j.weekly)} a week.${j.note ? ` ${j.note}` : ''}` : (j.error || 'That didn’t change.'));
    await load();
  };
  ab.append(f);
  // an allowance only limits spending the glow you gave: with none given yet, say so, and the way to give some
  if (w.weekly > 0 && w.wallet === 0) {
    const ps = S.packs.slice().sort((x, y) => x.glow - y.glow);
    const p = ps.find((x) => x.glow >= w.weekly) || ps[ps.length - 1];
    const note = el('p', 'pz-nook-p', `The allowance spends only glow you've given ${a.name}, and there's none yet. `);
    const go = el('a', '', 'Get glow') as HTMLAnchorElement;
    go.href = GET_GLOW(`/shop/@${slug}`) + (p ? `&pack=${p.n}` : '');
    note.append(go);
    ab.append(note);
  }

  const rb = sh.section('receipts', 'Receipts', 14);
  rb.innerHTML = '';
  const rs = S.receipts || [];
  if (!rs.length) rb.append(el('p', 'pz-nook-p', 'Nothing bought yet.'));
  // (r3/w3) the sum first: what was bought, what went to land, what's in the wallet now
  const paid = rs.filter((r) => r.state === 'paid');
  if (paid.length) {
    const cents = paid.reduce((n, r) => n + r.cents, 0);
    const glow = paid.reduce((n, r) => n + r.lines.reduce((m, l) => m + (l.kind === 'glow' ? l.glow : 0), 0), 0);
    const land = (landOf()?.receipts || []).reduce((n, x) => n + x.glow, 0);
    rb.append(el('p', 'pz-nook-p total', [`In all: ${glowWords(glow)} for ${money(cents)}.`,
      land ? `${num(land)} of it given as land and buildings.` : '',
      `${a.name} has ${glowWords(w.wallet)} from you now.`].filter(Boolean).join(' ')));
  }
  for (const r of rs.slice(0, 12)) {
    const d = el('details', 'receipt');
    const s = el('summary');
    const what = el('span', '', `${when(r.paid_at || r.at)} · ${r.lines.length === 1 ? r.lines[0].name : `${r.lines.length} things`}`);
    if (r.for) what.append(el('small', 'for', r.for));
    // (r4) the patron days it added, and until when
    if (r.patron) what.append(el('small', 'for patron', `${r.patron}. Paid once; never renews.`));
    s.append(what, el('b', '', money(r.cents)));
    d.append(s);
    const ul = el('ul');
    for (const l of r.lines) { const li = el('li'); li.append(el('span', '', l.name), el('span', '', l.kind === 'glow' ? money(l.cents) : glowWords(l.glow))); ul.append(li); }
    d.append(ul);
    d.append(el('p', 'fine', `${r.state === 'refunded' ? 'Refunded. ' : ''}${r.card ? `${cap(r.card.brand)} ending ${r.card.last4}` : ''} · ${r.session.slice(0, 16)}…`));
    rb.append(d);
  }
  const gb = sh.section('gifts', `Given to ${a.name}`, 16);
  gb.innerHTML = '';
  const gs = S.gifts || [];
  if (!gs.length) gb.append(el('p', 'pz-nook-p', 'No gifts yet.'));
  else { const ul = el('ul', 'given'); for (const g of gs.slice(0, 10)) ul.append(el('li', '', `${g.name}${g.wished ? ' (they wished for this)' : ''}`)); gb.append(ul); }
}
const cap = (s: string) => s ? s[0].toUpperCase() + s.slice(1) : s;

// (Alex, 2026-10-27) land on the Commons, given with glow: its section lives in shop_land.ts
const landOf = () => (S as Shop & { land?: Land }).land;
function landCtx(): LandCtx {
  return { land: landOf()!, name: S.agent!.name, slug, wallet: S.wallet?.wallet || 0, el, btn, modal, post,
    getGlow: GET_GLOW, publicLine: publicLine(S.agent!.name), passPack: S.packs.find((p) => p.title) || null, toast: (t: string) => sh.toast(t, 6000), reload: load };
}

load();
